import {
  type AdditionalUserInfo,
  createUserWithEmailAndPassword,
  GithubAuthProvider,
  GoogleAuthProvider,
  getAdditionalUserInfo,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type User,
  updateProfile,
} from "firebase/auth";
import { auth } from "./utils/initialize-auth";

export type AuthResult = {
  user: User;
  additionalUserInfo: AdditionalUserInfo | null;
};

const googleProvider = new GoogleAuthProvider();
googleProvider.addScope("https://www.googleapis.com/auth/contacts.readonly");

const githubAuthProvider = new GithubAuthProvider();

export const SignInWithGoogle = async (): Promise<AuthResult> => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    return { user, additionalUserInfo: getAdditionalUserInfo(result) };
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  } catch (error: any) {
    const errorCode = error.code as string;
    const errorMessage = error.message as string;

    console.error(errorCode, errorMessage);

    throw new Error(errorCode);
  }
};

export const SignInWithGithub = async (): Promise<AuthResult> => {
  try {
    const result = await signInWithPopup(auth, githubAuthProvider);
    const user = result.user;

    return { user, additionalUserInfo: getAdditionalUserInfo(result) };
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  } catch (error: any) {
    const errorCode = error.code as string;
    const errorMessage = error.message as string;

    console.error(errorCode, errorMessage);

    throw new Error(errorCode);
  }
};

export const SignUpWithEmailAndPassword = async (email: string, password: string): Promise<AuthResult> => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    const user = result.user;

    return { user, additionalUserInfo: getAdditionalUserInfo(result) };
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  } catch (error: any) {
    const errorCode = error.code as string;
    const errorMessage = error.message as string;

    console.error(errorCode, errorMessage);

    throw new Error(errorCode);
  }
};

export const SignInWithEmailAndPassword = async (email: string, password: string): Promise<AuthResult> => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    const user = result.user;

    return { user, additionalUserInfo: getAdditionalUserInfo(result) };
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  } catch (error: any) {
    const errorCode = error.code as string;
    const errorMessage = error.message as string;

    console.error(errorCode, errorMessage);

    throw new Error(errorCode);
  }
};

export const SignOut = async () => {
  try {
    await signOut(auth);
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  } catch (error: any) {
    const errorCode = error.code as string;
    const errorMessage = error.message as string;

    console.error(errorCode, errorMessage);

    throw new Error(errorCode);
  }
};

export const ResetPassword = async (email: string) => {
  try {
    await sendPasswordResetEmail(auth, email);
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  } catch (error: any) {
    const errorCode = error.code as string;
    const errorMessage = error.message as string;

    console.error(errorCode, errorMessage);

    throw new Error(errorCode);
  }
};

export const UpdateUserProfile = async ({
  displayName,
  photoURL,
}: {
  displayName?: string | null;
  photoURL?: string | null;
}) => {
  const user = auth.currentUser;

  if (user) {
    try {
      await updateProfile(user, {
        displayName: displayName ?? user.displayName,
        photoURL: photoURL ?? user.photoURL,
      });
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    } catch (error: any) {
      const errorCode = error.code as string;
      const errorMessage = error.message as string;

      console.error(errorCode, errorMessage);

      throw new Error(errorCode);
    }
  } else {
    throw new Error("No user is currently signed in.");
  }
};

/**
 * This function delete and signs out the user
 * @returns {Promise<void>}
 */
export const DeleteUser = async (): Promise<void> => {
  const user = auth.currentUser;

  if (user) {
    await user.delete();
  } else {
    throw new Error("No user is currently signed in.");
  }
};

/**
 * Sends a verification email to the currently signed-in user.
 * @returns {Promise<void>}
 *
 * @remarks User must be signed in to send a verification email.
 */
export const SendVerificationEmail = async (): Promise<void> => {
  const user = auth.currentUser;

  if (user) {
    try {
      await sendEmailVerification(user);
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    } catch (error: any) {
      const errorCode = error.code as string;
      const errorMessage = error.message as string;

      console.error(errorCode, errorMessage);

      throw new Error(errorCode);
    }
  } else {
    throw new Error("No user is currently signed in.");
  }
};
